import { useAuth } from "@/core/auth/hooks/useAuth";
import { useMemo } from "react";
import { useAvailableTimeSlots } from "@/features/availability/hooks/useAvailabilities";
import { generateAvailabilityTimeSlots } from "@/features/availability/utils/generateAvailabilityTimeSlots";
import type { TimeSlot } from "@/features/availability/types/availabilityTypes";
import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { useServices } from "@/features/services/hooks/serviceQueries";
import type { Service } from "@/features/services/types/serviceTypes";
import { useAppointments } from "./appointmentQueries";
import type { Appointment } from "../types/appointmentTypes";
import { businessDayBounds, hasBookingConflict } from "../utils/bookingTime";

export function useBookingAvailability(params: {
  dateKey: string;
  durationMinutes: number;
  before: number;
  after: number;
  locationId: string;
  excludeId?: string;
}) {
  const { business, businessId } = useCurrentBusiness();
  const { user } = useAuth();
  const zone = business?.timezone ?? "UTC";
  const bounds = useMemo(() => businessDayBounds(params.dateKey, zone), [params.dateKey, zone]);
  const availability = useAvailableTimeSlots({
    userId: user?.id ?? "", businessId: businessId ?? "", locationId: params.locationId,
    // Fetch surrounding days so buffered, cross-midnight visits are included by the service.
    startAt: new Date(bounds.startAt.getTime() - 86_400_000),
    endAt: new Date(bounds.endAt.getTime() + 86_400_000),
    excludeAppointmentId: params.excludeId,
  });
  // Only nearby bookings can conflict with slots on this day; pad for buffers and long visits.
  const conflictRange = useMemo(() => ({
    startAt: new Date(bounds.startAt.getTime() - 2 * 86_400_000),
    endAt: new Date(bounds.endAt.getTime() + 2 * 86_400_000),
  }), [bounds]);
  const appointments = useAppointments(conflictRange);
  const services = useServices();
  const calculateSlots = (windows: TimeSlot[], bookings: Appointment[], catalogue: Service[]) =>
    generateAvailabilityTimeSlots(windows, bounds.startAt, bounds.endAt, params.durationMinutes, params.before, params.after)
      .filter((slot) => slot.startAt.getTime() > Date.now() &&
        !hasBookingConflict(slot.startAt, slot.endAt, params.before, params.after, bookings, catalogue, params.locationId, params.excludeId));
  const error = availability.error ?? appointments.error ?? services.error;
  return {
    slots: error ? [] : calculateSlots(availability.data ?? [], appointments.data ?? [], services.data ?? []),
    error,
    isLoading: availability.isLoading || appointments.isLoading || services.isLoading,
    isFetching: availability.isFetching || appointments.isFetching || services.isFetching,
    enabled: !!user && !!businessId && !!params.locationId,
    async refresh() {
      if (!user || !businessId || !params.locationId) throw new Error("Choose a business and location before booking.");
      const [windows, bookings, catalogue] = await Promise.all([
        availability.refetch(), appointments.refetch(), services.refetch(),
      ]);
      if (windows.error || bookings.error || catalogue.error) throw windows.error ?? bookings.error ?? catalogue.error;
      if (!windows.data || !bookings.data || !catalogue.data) throw new Error("Availability could not be verified. Try again.");
      return {
        slots: calculateSlots(windows.data, bookings.data, catalogue.data),
        appointments: bookings.data,
        services: catalogue.data,
      };
    },
  };
}
